import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { isVideo, uploadVideo, fmtDuration } from "@/lib/videoUpload";
import { useAuth } from "@/context/AuthContext";
import { Btn } from "@/components/common";
import AuthImg from "@/components/foto/AuthImg";
import { ArrowLeft, Upload, Trash2, Download, Megaphone, Copy } from "lucide-react";

export default function GalleryDetail({ gid, onBack }) {
  const { isAdmin } = useAuth();
  const [g, setG] = useState(null);
  const [prog, setProg] = useState(null);
  const load = useCallback(() => { api.get(`/photo-galleries/${gid}`).then((r) => setG(r.data)); }, [gid]);
  useEffect(() => { load(); }, [load]);
  const upload = async (files) => {
    const list = [...files];
    let ok = 0;
    for (let i = 0; i < list.length; i++) {
      setProg({ done: i, total: list.length, name: list[i].name, pct: null });
      try {
        if (isVideo(list[i])) {
          await uploadVideo(gid, list[i], (pct) => setProg({ done: i, total: list.length, name: list[i].name, pct }));
        } else {
          const fd = new FormData();
          fd.append("file", list[i]);
          await api.post(`/photo-galleries/${gid}/photos`, fd);
        }
        ok++;
      } catch (e) { toast.error(`${list[i].name}: ${formatApiError(e)}`); }
    }
    setProg(null);
    if (ok) toast.success(`${ok} file caricati`);
    load();
  };
  const del = async (pid) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    try { await api.delete(`/photos/${pid}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const delGallery = async () => {
    if (!window.confirm(`Eliminare la galleria "${g.title}" e tutte le sue foto?`)) return;
    try { await api.delete(`/photo-galleries/${gid}`); toast.success("Galleria eliminata"); onBack(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const ads = async (p) => { await api.patch(`/photos/${p.id}`, { ads_ok: !p.ads_ok }); load(); };
  const download = async (p) => {
    try { window.location.href = `${API}${(await api.post(`/photos/${p.id}/download-link`)).data.url}`; }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!g) return null;
  return (
    <div data-testid="gallery-detail">
      <button onClick={onBack} data-testid="gallery-back" className="text-sm text-sky-700 font-semibold inline-flex items-center gap-1 mb-3"><ArrowLeft size={15} /> Tutte le gallerie</button>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="text-xl font-bold text-slate-900">{g.title}</h2>
        <span className="text-xs text-slate-500">{g.photos.filter((p) => p.kind !== "video").length} foto · {g.photos.filter((p) => p.kind === "video").length} video · fotografo {g.photographer_name || "—"} · scade il {g.expires_at.slice(0, 10).split("-").reverse().join("/")}</span>
      </div>
      <button onClick={delGallery} data-testid="gallery-delete" className="text-sm text-rose-600 font-semibold inline-flex items-center gap-1 mb-4 hover:text-rose-800"><Trash2 size={14} /> Elimina galleria</button>
      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 text-sm" data-testid="gallery-links">
          <div className="font-semibold text-slate-700 mb-1">Link di questa galleria per camera</div>
          {g.rooms?.length ? g.rooms.map((r) => (
            <div key={r.token} className="flex items-center gap-2 py-1">
              <span className="truncate">{r.label}</span>
              <button className="ml-auto text-sky-700 font-semibold inline-flex items-center gap-1" data-testid={`gallery-link-copy-${r.token}`} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/soggiorno/${r.token}`); toast.success("Link copiato"); }}><Copy size={13} /> Copia link</button>
            </div>
          )) : <div className="text-slate-400 text-xs">Nessuna camera: in "Camere: pagato / non pagato" segni la camera pagata e scelga questa galleria.</div>}
        </div>
      )}
      <label className="flex items-center justify-center gap-2 border-2 border-dashed border-sky-300 rounded-xl p-6 text-sm text-sky-700 font-semibold cursor-pointer hover:bg-sky-50 mb-4" data-testid="gallery-upload">
        <Upload size={18} /> {prog ? `Caricamento ${prog.done + 1}/${prog.total} · ${prog.name}${prog.pct != null ? ` · ${prog.pct}%` : ""}` : "Carica foto e video (JPG, PNG, WEBP, MOV, MP4 · video fino a 4 GB · più file insieme)"}
        <input type="file" multiple accept="image/jpeg,image/png,image/webp,video/*,.mov,.mp4,.m4v,.3gp" className="hidden" disabled={!!prog} onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="gallery-upload-input" />
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {g.photos.map((p) => (
          <div key={p.id} className="relative rounded-lg overflow-hidden border border-slate-200 bg-white" data-testid={`photo-${p.id}`}>
            <AuthImg src={`/photos/${p.id}/preview`} alt={p.filename} className="w-full aspect-square object-cover" />
            <div className="flex items-center gap-2 px-2 py-1.5 text-xs">
              <span className="truncate text-slate-500 flex-1">{p.filename}</span>
              {isAdmin && <button title="Autorizzata per le ADS" data-testid={`photo-ads-${p.id}`} onClick={() => ads(p)} className={p.ads_ok ? "text-violet-600" : "text-slate-300"}><Megaphone size={14} /></button>}
              <button title="Scarica originale" data-testid={`photo-dl-${p.id}`} onClick={() => download(p)} className="text-sky-700"><Download size={14} /></button>
              <button title="Elimina" data-testid={`photo-del-${p.id}`} onClick={() => del(p.id)} className="text-rose-500"><Trash2 size={14} /></button>
            </div>
            {p.ads_ok && <span className="absolute top-1 left-1 text-[10px] font-bold bg-violet-600 text-white px-1.5 rounded">ADS</span>}
            {p.kind === "video" && <span className="absolute top-1 right-1 text-[10px] font-bold bg-slate-900/80 text-white px-1.5 rounded" data-testid={`photo-video-${p.id}`}>VIDEO {fmtDuration(p.duration)}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
